import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { Patch } from '../manual/patchBuilders';
import { ScenarioScope, scopeBase, scopeKey, useScenarioScope } from '../scope';
import {
  ChartPaletteConfig,
  ProfileSyncStatus,
  ScenarioCommit,
  ScenarioCreateResult,
  ScenarioDetail,
  ScenarioTransferResult,
  SyncMode,
} from '../types';

type ScenarioUpdateBody = {
  label?: string;
  isPrimary?: boolean;
  archived?: boolean;
  chartPalette?: ChartPaletteConfig;
  clearChartPalette?: boolean;
  syncMode?: SyncMode;
};

type SyncScenarioBody = {
  expectedLastSeq?: number;
};

type ApplyCommitBody = {
  patches: Patch[];
  intent: string;
  rationale?: string;
  expectedLastSeq: number;
};

type CreateScenarioBody = {
  label?: string;
  // Family scope only: the one or two adult members to include.
  memberClientIds?: string[];
};

type TransferToFamilyBody = {
  // Only needed when the family has more than one other adult.
  partnerClientId?: string;
  expectedLastSeq: number;
};

const isClientCashflowQuery = ({ queryKey }: { queryKey: readonly unknown[] }): boolean =>
  queryKey[0] === 'clients' && queryKey[2] === 'cashflow-scenarios';

export function useScenarioMutations(scenarioId: string | undefined) {
  const scope = useScenarioScope();
  const queryClient = useQueryClient();

  // Queries guard themselves with `enabled`; mutations must fail loudly
  // rather than PATCH ".../undefined".
  const requireScenarioId = (): string => {
    if (!scenarioId) throw new Error('No cashflow model selected');
    return scenarioId;
  };
  const requireScope = (): ScenarioScope => {
    if (!scope) throw new Error('No cashflow model scope available');
    return scope;
  };

  // A family model also shows as a card on each included member's client page,
  // which reads it through that client's own list. Only the cashflow queries
  // are refreshed: this runs on every edit, and everything else under
  // `clients` is untouched by a model change.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: scope ? scopeKey(scope) : ['clients'] }),
      scope?.kind === 'family'
        ? queryClient.invalidateQueries({ predicate: isClientCashflowQuery })
        : undefined,
    ]);

  const { mutateAsync: updateScenario } = useMutation({
    mutationFn: async (body: ScenarioUpdateBody) => {
      const { data } = await api.patch<ScenarioDetail>(
        `${scopeBase(requireScope())}/cashflow-scenarios/${requireScenarioId()}`,
        body,
      );
      return data;
    },
    onSuccess: invalidate,
  });

  const { mutateAsync: applyCommit } = useMutation({
    mutationFn: async (body: ApplyCommitBody) => {
      const { data } = await api.post<ScenarioCommit>(
        `${scopeBase(requireScope())}/cashflow-scenarios/${requireScenarioId()}/commits`,
        body,
      );
      return data;
    },
    onSuccess: invalidate,
  });

  const { mutateAsync: revertCommit } = useMutation({
    mutationFn: async ({ seq, expectedLastSeq }: { seq: number; expectedLastSeq: number }) => {
      const { data } = await api.post<ScenarioCommit>(
        `${scopeBase(requireScope())}/cashflow-scenarios/${requireScenarioId()}/commits/${seq}/revert`,
        { expectedLastSeq },
      );
      return data;
    },
    onSuccess: invalidate,
  });

  // "Refresh now": applies the profile's changes whatever the mode and moves
  // the projection start to today.
  const { mutateAsync: syncScenario } = useMutation({
    mutationFn: async (body: SyncScenarioBody) => {
      const { data } = await api.post<ProfileSyncStatus>(
        `${scopeBase(requireScope())}/cashflow-scenarios/${requireScenarioId()}/sync`,
        body,
      );
      return data;
    },
    onSuccess: invalidate,
  });

  return { updateScenario, applyCommit, revertCommit, syncScenario };
}

// Client scope only: moves an individual model into the client's family. The
// model then lives under the family's queries, and shows on every included
// member's client page, so both caches are invalidated.
export function useTransferToFamily(scenarioId: string | undefined) {
  const scope = useScenarioScope();
  const queryClient = useQueryClient();

  const { mutateAsync: transferToFamily, isPending: isTransferring } = useMutation({
    mutationFn: async (body: TransferToFamilyBody) => {
      if (!scope || scope.kind !== 'client')
        throw new Error('Only a client model can be moved into a family');
      if (!scenarioId) throw new Error('No cashflow model selected');
      const { data } = await api.post<ScenarioTransferResult>(
        `${scopeBase(scope)}/cashflow-scenarios/${scenarioId}/transfer-to-family`,
        body,
      );
      return data;
    },
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (result.scenario.familyId) {
        queryClient.invalidateQueries({
          queryKey: scopeKey({ kind: 'family', id: result.scenario.familyId }),
        });
      }
    },
  });

  return { transferToFamily, isTransferring };
}

export function useCreateScenario(scopeOverride?: ScenarioScope) {
  const contextScope = useScenarioScope();
  const queryClient = useQueryClient();
  const scope = scopeOverride ?? contextScope;

  const { mutateAsync: createScenario, isPending: isCreating } = useMutation({
    mutationFn: async (body: CreateScenarioBody) => {
      if (!scope) throw new Error('No cashflow model scope available');
      const { data } = await api.post<ScenarioCreateResult>(
        `${scopeBase(scope)}/cashflow-scenarios`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      if (scope) {
        queryClient.invalidateQueries({ queryKey: scopeKey(scope) });
      }
      // A family model also appears on each included member's client page.
      if (scope?.kind === 'family') {
        queryClient.invalidateQueries({ queryKey: ['clients'] });
      }
    },
  });

  return { createScenario, isCreating };
}
